/**
 * Category Settings Store - per-category configuration
 * Supports: subconditions (individual Original/3rd Party/Both/Off), custom labels, grade visibility
 */

const CATEGORY_SETTINGS_KEY = "khays_category_settings";

export type SubconditionMode = "both" | "original_only" | "third_party_only" | "off";

export interface CategorySettings {
  subconditionsEnabled: boolean; // legacy — kept for backward compat
  subconditionMode?: SubconditionMode; // new granular mode
  modelLabel?: string;
  gradeLabel?: string;
  gradesEnabled?: boolean;
  noteText?: string;
  noteWhatsAppCta?: string;
}

function loadAll(): Record<string, CategorySettings> {
  try {
    const stored = localStorage.getItem(CATEGORY_SETTINGS_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return {};
}

function saveAll(settings: Record<string, CategorySettings>): void {
  localStorage.setItem(CATEGORY_SETTINGS_KEY, JSON.stringify(settings));
}

export function getCategorySettings(category: string): CategorySettings {
  return loadAll()[category] || { subconditionsEnabled: true };
}

/** Get resolved subcondition mode for a category */
export function getCategorySubconditionMode(category: string): SubconditionMode {
  const s = getCategorySettings(category);
  if (s.subconditionMode) return s.subconditionMode;
  // Backward compat: convert legacy boolean
  return s.subconditionsEnabled ? "both" : "off";
}

export function setCategorySubconditionMode(category: string, mode: SubconditionMode): void {
  const all = loadAll();
  if (!all[category]) all[category] = { subconditionsEnabled: true };
  all[category].subconditionMode = mode;
  // Keep legacy in sync
  all[category].subconditionsEnabled = mode !== "off";
  saveAll(all);
}

/** @deprecated Use setCategorySubconditionMode */
export function setCategorySubconditions(category: string, enabled: boolean): void {
  setCategorySubconditionMode(category, enabled ? "both" : "off");
}

export function setCategoryModelLabel(category: string, label: string): void {
  const all = loadAll();
  if (!all[category]) all[category] = { subconditionsEnabled: true };
  all[category].modelLabel = label || undefined;
  saveAll(all);
}

export function setCategoryGradeLabel(category: string, label: string): void {
  const all = loadAll();
  if (!all[category]) all[category] = { subconditionsEnabled: true };
  all[category].gradeLabel = label || undefined;
  saveAll(all);
}

export function setCategoryGradesEnabled(category: string, enabled: boolean): void {
  const all = loadAll();
  if (!all[category]) all[category] = { subconditionsEnabled: true };
  all[category].gradesEnabled = enabled;
  saveAll(all);
}

export function setCategoryNoteText(category: string, noteText: string): void {
  const all = loadAll();
  if (!all[category]) all[category] = { subconditionsEnabled: true };
  all[category].noteText = noteText || undefined;
  saveAll(all);
}

export function setCategoryNoteWhatsAppCta(category: string, ctaText: string): void {
  const all = loadAll();
  if (!all[category]) all[category] = { subconditionsEnabled: true };
  all[category].noteWhatsAppCta = ctaText || undefined;
  saveAll(all);
}

export function getModelLabel(category: string): string {
  const settings = getCategorySettings(category);
  return settings.modelLabel || "Device Model";
}

export function getGradeLabel(category: string): string {
  const settings = getCategorySettings(category);
  return settings.gradeLabel || "Condition Grade";
}

export function areGradesEnabled(category: string): boolean {
  const settings = getCategorySettings(category);
  return settings.gradesEnabled !== false;
}

export function getAllCategorySettings(): Record<string, CategorySettings> {
  return loadAll();
}

/** Device-level subcondition mode */
export type DeviceSubconditionMode = "inherit" | "both" | "original_only" | "third_party_only" | "off";

const DEVICE_SUBCONDITION_MODE_KEY = "khays_device_subcondition_modes";

function loadDeviceSubconditionModes(): Record<string, DeviceSubconditionMode> {
  try {
    const s = localStorage.getItem(DEVICE_SUBCONDITION_MODE_KEY);
    if (s) return JSON.parse(s);
  } catch {}
  return {};
}

function saveDeviceSubconditionModes(modes: Record<string, DeviceSubconditionMode>): void {
  localStorage.setItem(DEVICE_SUBCONDITION_MODE_KEY, JSON.stringify(modes));
}

export function getDeviceSubconditionMode(deviceName: string): DeviceSubconditionMode {
  return loadDeviceSubconditionModes()[deviceName] || "inherit";
}

export function setDeviceSubconditionMode(deviceName: string, mode: DeviceSubconditionMode): void {
  const all = loadDeviceSubconditionModes();
  if (mode === "inherit") delete all[deviceName];
  else all[deviceName] = mode;
  saveDeviceSubconditionModes(all);
}

/**
 * Resolve which screen conditions are shown for a device.
 * Returns the list of enabled screen conditions, or empty array if subconditions are off.
 */
export function getEffectiveScreenConditions(
  category: string,
  deviceName: string,
  deviceSubconditionsEnabled: boolean
): string[] {
  // First check device-level mode override
  const deviceMode = getDeviceSubconditionMode(deviceName);
  
  let effectiveMode: SubconditionMode;
  
  if (deviceMode !== "inherit") {
    effectiveMode = deviceMode as SubconditionMode;
  } else {
    // Use category mode, but only if device-level subconditions are also enabled
    if (!deviceSubconditionsEnabled) return [];
    effectiveMode = getCategorySubconditionMode(category);
  }
  
  switch (effectiveMode) {
    case "both": return ["Original Screen", "3rd Party Screen"];
    case "original_only": return ["Original Screen"];
    case "third_party_only": return ["3rd Party Screen"];
    case "off": return [];
    default: return [];
  }
}

/**
 * Resolve whether subconditions are enabled for a device (legacy compat).
 */
export function isSubconditionEnabled(category: string, deviceSubconditionsEnabled: boolean): boolean {
  const catMode = getCategorySubconditionMode(category);
  return catMode !== "off" && deviceSubconditionsEnabled;
}
